import { Component, inject, Input, SimpleChanges, ViewChild, WritableSignal } from '@angular/core';
import { IRptEspCobClm, IRptEspCobDet, IRptEspCobPay } from '../../../shared/interfaces/rptEspCob';
import { MatTableDataSource } from '@angular/material/table';
import { ModalService } from '../../../shared/modal.service';
import { RecordCaseModalComponent } from '../../../shared/record-case-modal/record-case-modal.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { RecordComponent } from '../../../record/record.component';
import { MatSort } from '@angular/material/sort';

interface IExpandedCol {
  index: number;
  show: boolean;
  details: IRptEspCobDet[];
}

@Component({
  selector: 'app-rpt-esp-cob-clm',
  imports: [
    RecordCaseModalComponent,
    RecordComponent
  ],
  templateUrl: './rpt-esp-cob-clm.component.html',
  styleUrl: '../rpt-esp-cob.component.css',
  standalone: false
})
export class RptEspCobClmComponent {
  @Input() claims: IRptEspCobClm[];
  @Input() details: IRptEspCobDet[];
  @Input() payments: IRptEspCobPay[];
  @Input() sioQuery!: WritableSignal<string | null>;
  @Input() engLang: boolean;

  @ViewChild(MatSort) sort: MatSort;

  _modalService = inject(ModalService);
  _ngbModal = inject(NgbModal);

  dataSource: MatTableDataSource<IRptEspCobClm> = new MatTableDataSource<IRptEspCobClm>();

  sn: string;
  userID: string;
  userLastNm: string;
  userFirstNm: string;

  modalStack: string[] = [];

  activeView: string = ''; // Holds the currently selected view name
  detail: IRptEspCobDet[] = [];
  payment: IRptEspCobPay[] = [];
  showDetails: IExpandedCol[] = []; // Array to track which rows have details expanded
  showD: boolean = false; // For *ngIf in template

  rptEspCobClmDisplydCols: string[] = [
    "CasNo",
    "CasOfNo",
    "ps",
    "patNm",
    "casUsual",
    "casXpect",
    "casIPayed",
    "casBalance",
    "casBPvdo",
    "casDt"
  ];

  constructor() {
    this.dataSource = new MatTableDataSource<IRptEspCobClm>(this.claims ?? []);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['claims'] && this.claims) {
      this.dataSource.data = this.claims.slice(); // Create a new array to avoid reference issues
      this.dataSource.sort = this.sort; // Always follow this.dataSource = ... with this to maintain header sort arrow synced

    }
    if (changes['engLang']) {
      this.engLang = changes['engLang'].currentValue;
    }
  }

  onClick_expandClm(cID: string, ps: string): void {
    // 1) grab the current clm row
    const current = this.claims;
    if (!current) return; // Get out if no data

    const idx = current.findIndex(clm => clm.CasID === cID); // Sync clm & idx index

    if (!this.showDetails[idx]?.details.length) {
      this.detail = this.details.filter(det => det.keyDetCasID.endsWith(cID) && det.keyDetCasID.startsWith(ps));
      this.payment = this.payments.filter(pay => pay.keyPayCasID.endsWith(cID) && pay.keyPayCasID.startsWith(ps));
      this.showDetails[idx] = { index: idx, show: true, details: this.detail }; // Update showDetail with details
      this.showD = true; // Set show to true when details are expanded

      if (!this.detail && !this.detail.length) {  // But if it is an empty claim never expand it or you cannot ever collapse it
        this.showDetails[idx] = { index: idx, show: false, details: this.detail };
        this.showD = false; // Don't expand if no details or payments
      }
    } else {
      this.detail = []; // Clear detail for this cid
      this.payment = [];  // Clear payment for this cid
      this.showDetails[idx] = { index: idx, show: false, details: this.detail };
      this.showD = false; // Reset show to false when details are collapsed
    }
  }

  isExpanded(row): boolean {  // Call from expanded row in template
    if (!row) return false; // If row is undefined or null, return false

    const current = this.claims; // Get the current claims
    if (!current) return false; // Get out if no data

    const idx = current.findIndex(clm => row?.keyCasID?.startsWith(clm.ps) && row?.keyCasID?.endsWith(clm.CasID)); // Current row index
    // console.log('idx', idx);

    this.detail = this.showDetails[idx]?.details || [];
    this.showD = this.showDetails[idx]?.show;  // Previously set showC to true or false based on the expanded state in onClick_expandIns(...)

    return this.showD;  // Expand (true) or contract (false) details
  }

  open(content, opts, modalNm) {
    this.modalStack.push(modalNm);
    const modal$ = this._modalService.open(content, opts);
    modal$.subscribe({
      next: (result) => {    // Save, Ok, etc
        console.log('result', result);
        const qry = "EXEC dbo.spRptDtx @viewNm = '" + this.activeView + "';";
        window.scroll(0, 0);
        console.log('%c' + 'query ' + qry, 'color: green; background: yellow; font-size: 12px');
        this.sioQuery.set(qry);
        this.modalStack.pop();
      },
      error: (reason) => {    // dismiss, X, Close, Esc
        console.log('reason', reason);
        console.log('modalStack', this.modalStack);
        switch (this.modalStack[this.modalStack.length - 1]) {
          case 'recordCaseModal':
            // Do some cleanup
            break;
          default:
        }
        this.modalStack.pop();
      }
    });
  }
  openRecordCaseModal(patNm: string, casNo: string, engLang: boolean, xtPID: number, xtCID: number): void {
    const modalRef: NgbModalRef = this._ngbModal.open(RecordCaseModalComponent, { backdrop: 'static', size: 'lg', scrollable: true });
    Object.assign(modalRef.componentInstance, {
      patNmModalHd: patNm,
      casNoModalHd: casNo,
      engLang: engLang,
      xtPID: xtPID,
      xtCID: xtCID
    });

    modalRef.result.then( // Callback for when the modal is closed
      result => console.log('Got back', result),
      reason => console.log('Dismissed', reason)
    );
  }

  sortea(sort) {
    const arr2sort: any[] = this.dataSource.data.slice();

    if (sort.active === 'patNm' || sort.active === 'casBalance' || sort.active === 'casBPvdo' || sort.active === 'CasNo' || sort.active === 'casDt' || sort.active === 'ps') {
      if (sort.direction === 'asc') {
        if (sort.active === 'casDt') {
          arr2sort.sort((a, b) => new Date(a.casDt).getTime() < new Date(b.casDt).getTime() ? 1 : -1);
        } else {
          arr2sort.sort((a, b) => a[sort.active] < b[sort.active] ? 1 : -1);
        }
      } else {
        if (sort.active === 'casDt') {
          arr2sort.sort((a, b) => new Date(a.casDt).getTime() > new Date(b.casDt).getTime() ? 1 : -1);
        } else {
          arr2sort.sort((a, b) => a[sort.active] > b[sort.active] ? 1 : -1);
        }
      }
    }

    this.dataSource = new MatTableDataSource(arr2sort.slice());
    this.dataSource.sort = this.sort; // Always follow this.dataSource = ... with this to maintain header sort arrow synced
  }

}